import { useQuery } from '@tanstack/react-query';
import { privateApi } from '../../../api/api';

// Impor skema dan tipe yang relevan.
// Kita akan menggunakan skema PropertyV4 yang sudah ada.
import { PropertyV4Schema } from '../schemas/UserV4Schema';
import { z } from 'zod';

const API_ENDPOINT = '/v4/property';

// Skema untuk memvalidasi respons dari endpoint get all properties.
// Diasumsikan responsnya tidak memiliki paginasi jika 'all=1' digunakan.
const GetAllPropertiesV4ResponseSchema = z.object({
    success: z.boolean(),
    message: z.string(),
    code: z.number(),
    data: z.array(PropertyV4Schema), // Respons adalah array dari properti
});

// Tipe untuk keseluruhan respons
type GetAllPropertiesV4Response = z.infer<typeof GetAllPropertiesV4ResponseSchema>;

/**
 * Hook kustom untuk mengambil daftar LENGKAP semua properti (Versi 4).
 * Data ini digunakan untuk dropdown dan pilihan di seluruh aplikasi.
 * 
 * @returns Hasil dari React Query, termasuk data properti, status loading, dan error.
 */
export const useGetAllPropertiesV4 = () => {

    return useQuery<GetAllPropertiesV4Response, Error>({
        /**
         * Kunci query yang unik untuk daftar semua properti.
         * Dengan kunci yang statis ini, data akan di-fetch sekali dan di-cache
         * untuk digunakan kembali di seluruh aplikasi.
         */
        queryKey: ['properties', 'v4', 'all'],

        /**
         * Fungsi yang akan dieksekusi oleh React Query untuk mengambil data.
         */
        queryFn: async (): Promise<GetAllPropertiesV4Response> => {
            try {
                // Melakukan request GET dengan parameter 'all=1' untuk mendapatkan semua data.
                const response = await privateApi.get(API_ENDPOINT, {
                    params: {
                        all: 1
                    }
                });

                // Validasi struktur data respons dari API.
                const validationResult = GetAllPropertiesV4ResponseSchema.safeParse(response.data);

                if (!validationResult.success) {
                    console.error("API response validation failed in useGetAllPropertiesV4:", validationResult.error.flatten());
                    throw new Error("Invalid data format received from the server for all properties.");
                }

                // Kembalikan data yang sudah tervalidasi.
                return validationResult.data;
            } catch (error) {
                console.error('Failed to fetch all properties (V4):', error);
                throw error;
            }
        },

        /**
         * staleTime: Mengatur berapa lama data di cache dianggap "fresh" (segar).
         * Dengan 5 menit, React Query tidak akan me-refetch data ini pada setiap
         * window focus atau re-mount komponen selama data masih dalam 5 menit.
         * Ini adalah optimasi yang sangat baik untuk data yang jarang berubah.
         */
        staleTime: 1000 * 60 * 5, // 5 menit

        // Opsi tambahan untuk React Query
        refetchOnWindowFocus: false, // Tidak perlu refetch data ini saat window fokus
        retry: 1, // Mencoba lagi sekali jika gagal
    });
};